import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, debounceTime, filter, firstValueFrom, map, take } from 'rxjs';
import { pairwise, startWith, switchMap, tap } from 'rxjs/operators';

import { CompaniesService } from '@app/services/companies.service';
import { UserService } from '@app/services/user.service';
import { loadPropertyUsers } from '@main-application/administration/actions/administration.actions';
import {
  selectPropertyUsers,
  selectPropertyUsersLoading,
} from '@main-application/administration/selectors/administration.selectors';
import { BoardsAbstract } from '@main-application/boards/abstract/board-abstract.component';
import { BoardColumn, RestTurnStepScheduleModel } from '@main-application/boards/interfaces/board';
import { ScheduleCellStatus } from '@main-application/boards/interfaces/board.enums';
import { ColumnSettings } from '@main-application/boards/interfaces/boards-configuration';
import { BoardEventService } from '@main-application/boards/services/board-event.service';
import { CustomInspectionService } from '@main-application/boards/services/custom-inpection.service';
import { InspectionType } from '@main-application/inspections/models/rest-inspections-model.interface';
import { TimezoneService } from '@main-application/management/components/system-configuration/components/date-time-configuration/timezone.service';
import { getCompanyList } from '@main-application/turnovers/actions/turnovers.actions';
import { selectCompanyList } from '@main-application/turnovers/selectors/turnovers.selectors';
import { ButtonType } from '@shared/enums/button-type';
import { UserType } from '@shared/enums/user-type';
import { getCompanyRadioList } from '@shared/functions/get-company-radio-list.function';
import { getEmployeeRadioList } from '@shared/functions/get-employee-radio-list.function';
import { RestCompanyModel } from '@shared/interfaces/companies.interface';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestTurnoverTaskModel } from '@shared/interfaces/turnover.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { selectTenantData } from '@shared/selectors/user.selectors';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { ContentEditableAreaComponent } from '@ui-components/components/content-editable-area/content-editable-area.component';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

export interface ScheduleCellDialogData {
  cellData: RestTurnStepScheduleModel;
  column: BoardColumn;
  turnover: RestTurnoverTaskModel;
  boardEventService: BoardEventService;
}

export class ScheduleCellDialogResult extends RestTurnStepScheduleModel {
  assigneeEmail: string;
  newCustomTemplate: boolean;
}

const NewCustomTemplateOption: IRadioButtonOption<number> = {
  value: -1,
  label: 'Custom Inpsection',
};
@UntilDestroy()
@Component({
  selector: 'app-schedule-cell-dialog',
  templateUrl: './schedule-cell-dialog.component.html',
  styleUrls: ['./schedule-cell-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleCellDialogComponent extends BoardsAbstract implements OnInit {
  appendTo = 'app-boards';
  form = this.formBuilder.group({
    dueDate: [null as Date, [Validators.required]],
    isVendor: [false],
    assignee: [null as number],
    vendor: [null as number],
    remember: [false],
    notifyAssignee: [false],
    note: [null as string],
    inspectionTemplateId: [null as number],
  });
  formReady = false;
  showNote = false;
  ButtonType = ButtonType;
  today = this.timezoneService.getCurrentDateOnly();
  userListModel: RestUserModel[] = [];
  vendorList: IRadioButtonOption<any>[] = [];
  vendorAssigneeList: IRadioButtonOption<any>[] = [];
  propertyUserListLoading$: Observable<boolean>;
  isReschedule: boolean;
  columnConfiguration: ColumnSettings;
  systemConfigPmsName$ = this.store.select(selectTenantData).pipe(
    untilDestroyed(this),
    filter(tenantData => !!tenantData),
    take(1),
    map(tenantData => tenantData.pmses.split(',')[0])
  );

  inspectionTemplatesOptions: IRadioButtonOption<number>[][];
  inspectionTemplates: RestTemplateModel[];
  isInspectionTemplatesLoading$: Observable<boolean>;

  @ViewChild('commentArea') editInput: ContentEditableAreaComponent;

  constructor(
    public dialogRef: MatDialogRef<ScheduleCellDialogComponent, ScheduleCellDialogResult>,
    private formBuilder: FormBuilder,
    protected store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    private timezoneService: TimezoneService,
    private modalsService: ModalsService,
    protected snackbarService: SnackbarService,
    private companiesService: CompaniesService,
    private userService: UserService,
    private customInspectionService: CustomInspectionService,
    @Inject(MAT_DIALOG_DATA) protected data: ScheduleCellDialogData
  ) {
    super(store, snackbarService);
    this.isReschedule = !!this.data.cellData.id;
  }

  ngOnInit(): void {
    this.propertyUserListLoading$ = this.store.select(selectPropertyUsersLoading).pipe(debounceTime(200));
    this.store.dispatch(loadPropertyUsers({ propertyId: this.data.turnover.propertyId }));
    this.store.dispatch(getCompanyList());

    // should be before initialize, order matters
    this.form.controls.inspectionTemplateId.valueChanges.pipe(startWith(null), pairwise()).subscribe(([prev, next]) => {
      if (next === NewCustomTemplateOption.value && !this.checkEditRight()) {
        this.form.patchValue({ inspectionTemplateId: prev });
      }
    });
    this.initialize();

    this.store
      .select(selectCompanyList)
      .pipe(
        untilDestroyed(this),
        filter((companyList: RestCompanyModel[]) => !!companyList),
        tap((companyList: RestCompanyModel[]) => {
          this.vendorList = getCompanyRadioList(companyList);
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectPropertyUsers)
      .pipe(
        untilDestroyed(this),
        filter((users: RestUserModel[]) => !!users)
      )
      .subscribe((users: RestUserModel[]) => {
        this.userListModel = users;
        this.cdr.detectChanges();
      });

    this.form.controls.assignee.valueChanges.subscribe(value => this.form.controls.notifyAssignee.setValue(!!value));

    this.form.controls.isVendor.valueChanges.subscribe(value => {
      if (!this.formReady) {
        return;
      }
      if (value) {
        this.form.controls.assignee.setValue(null);
      } else {
        this.form.controls.vendor.setValue(null);
        this.form.controls.assignee.setValue(null);
      }
    });

    this.form.controls.vendor.valueChanges.subscribe((vendorId: number | null) => {
      this.vendorAssigneeList = [];
      if (this.formReady) {
        this.form.controls.assignee.setValue(null);
      }
      if (vendorId) {
        this.companiesService.getCompany(vendorId).subscribe(company => {
          this.vendorAssigneeList = getEmployeeRadioList(company.employees);
          this.cdr.detectChanges();
        });
      }
      this.cdr.detectChanges();
    });

    this.data.boardEventService.getBoardConfig().subscribe(boardConfig => {
      this.columnConfiguration = boardConfig.columnSettings[this.data.column.id];

      if (this.columnConfiguration?.rememberAssignee) {
        const patchValue: typeof this.form.value = {
          notifyAssignee: this.columnConfiguration?.notifyAssignee,
          remember: this.columnConfiguration?.rememberAssignee,
        };

        if (!this.isReschedule) {
          patchValue.assignee = this.columnConfiguration?.assignee;
        }
        patchValue.inspectionTemplateId = this.columnConfiguration.inspectionTemplateId;

        this.form.patchValue(patchValue, { emitEvent: false });
      }
    });

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(formValue => formValue.remember)
      )
      .subscribe(formValue => {
        this.data.boardEventService.updateColumnStore(this.data.column.id, {
          assignee: formValue.assignee,
          rememberAssignee: formValue.remember,
          notifyAssignee: formValue.notifyAssignee,
          inspectionTemplateId: formValue.inspectionTemplateId,
        });
      });
  }

  initialize() {
    this.isInspectionTemplatesLoading$ = this.customInspectionService.templatesLoading$.pipe(debounceTime(200));
    this.customInspectionService
      .getInspectionTemplates(this.data.cellData?.inspectionTemplateId)
      .pipe(
        tap(templatesList => {
          this.inspectionTemplates = templatesList;

          this.inspectionTemplatesOptions = [
            [NewCustomTemplateOption],
            this.inspectionTemplates.map(template => ({ label: template.name, value: template.id })) ?? [],
          ];
          if (!templatesList.find(template => template.id === this.form.value.inspectionTemplateId)) {
            this.form.patchValue({ inspectionTemplateId: undefined }, { emitEvent: false });
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe();

    if (this.data?.cellData.assignee) {
      this.userService.getUserById(this.data?.cellData.assignee).subscribe(user => {
        this.initForm(user.userGroupType === UserType.Contractor ? user.companyId : null);
      });
    } else {
      this.initForm(null);
    }
  }

  initForm(vendor: number | null) {
    this.form.patchValue({
      ...this.data?.cellData,
      // reschedule uses existing template id, schedule uses column settings
      inspectionTemplateId: this.data.cellData?.inspectionTemplateId ?? this.data.column?.inspectionTemplateId,
      isVendor: !!vendor,
      vendor,
      dueDate: this.data.cellData.dueDate ?? this.today,
    });
    this.formReady = true;
    this.cdr.markForCheck();
  }

  async ok() {
    if (this.form.valid) {
      const cellValue = this.form.value;
      const { dateMoveIn } = this.data.turnover.turnoverData;
      if (dateMoveIn && cellValue.dueDate > dateMoveIn) {
        this.modalsService
          .openConfirmationModal({
            title: 'Are you sure this isn’t too late',
            content: `This date is after tenant’s expected Move In Date of ${moment(dateMoveIn).format('MM/DD/YYYY')}`,
            cancelLabel: 'Cancel',
            confirmLabel: 'Yes, I am sure',
          })
          .afterClosed()
          .pipe(
            filter(result => result === DialogResult.Success),
            switchMap(() =>
              this.modalsService
                .openConfirmationModal({
                  title: 'Are you sure this isn’t too late',
                  content: `Are you really really sure?`,
                  cancelLabel: 'Cancel',
                  confirmLabel: 'Yes, I am really sure',
                })
                .afterClosed()
            ),
            filter(result => result === DialogResult.Success)
          )
          .subscribe(async () => {
            this.dialogRef.close(await this.getDialogResult());
            this.systemConfigPmsName$.subscribe(pmsName =>
              this.snackbarService.warning(
                `Remember to change the Move In Date in ${pmsName} to after ${moment(cellValue.dueDate).format(
                  'MM/DD/YYYY'
                )}!`
              )
            );
          });
      } else {
        this.dialogRef.close(await this.getDialogResult());
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  async getDialogResult(): Promise<ScheduleCellDialogResult> {
    const cellValue = this.form.value;
    let inspectionTemplateId: number;
    if (cellValue.inspectionTemplateId === NewCustomTemplateOption.value) {
      [inspectionTemplateId] = await this.addNewCustomTemplate();
    } else {
      inspectionTemplateId = cellValue.inspectionTemplateId;
    }

    cellValue.notifyAssignee = cellValue.notifyAssignee && !!cellValue.assignee;
    cellValue.note = cellValue.notifyAssignee ? cellValue.note : null;
    return {
      ...cellValue,
      status: cellValue.dueDate < this.today ? ScheduleCellStatus.Late : ScheduleCellStatus.Due,
      inspectionTemplateId: inspectionTemplateId,
      inspectionType: cellValue.inspectionTemplateId
        ? cellValue.inspectionTemplateId === NewCustomTemplateOption.value
          ? InspectionType.CustomInspection
          : this.inspectionTemplates.find(template => template.id === cellValue.inspectionTemplateId)?.inspectionType
        : undefined,
      assigneeEmail: this.userListModel.find(e => e.id === cellValue.assignee)?.email,
      newCustomTemplate: cellValue.inspectionTemplateId === NewCustomTemplateOption.value,
    } as ScheduleCellDialogResult;
  }

  async addNewCustomTemplate() {
    const customInspectionMaxNumber = this.inspectionTemplates
      .filter(e => /^Custom Inspection[0-9]*$/.test(e.name))
      .map(c => +c.name.replace('Custom Inspection', ''))
      .reduce((prev, max) => (prev > max ? prev : max), 0);

    return await firstValueFrom(
      this.customInspectionService.addCustomInspectionTemplateWithPredefinedItems({
        inspectionName: `Custom Inspection${customInspectionMaxNumber + 1}`,
        isRequired: false,
      })
    );
  }

  cancel() {
    this.dialogRef.close();
  }

  focusOnComment() {
    if (this.showNote) {
      this.form.controls.notifyAssignee.setValue(true);
      if (this.editInput?.editInput?.nativeElement) {
        this.editInput.editInput.nativeElement.focus();
      } else {
        setTimeout(() => this.editInput?.editInput?.nativeElement?.focus(), 1);
      }
    }
  }

  addNewVendor() {
    this.modalsService
      .openNewVendorModal({})
      .afterClosed()
      .pipe(
        tap(company => {
          if (company) {
            this.form.controls.vendor.setValue(company.id);
            this.companiesService.getCompany(company.id).subscribe(company => {
              this.vendorAssigneeList = getEmployeeRadioList(company.employees);
              if (this.vendorAssigneeList.length) {
                this.form.controls.assignee.setValue(this.vendorAssigneeList[0].value);
              }
              this.cdr.detectChanges();
            });
          }
          this.store.dispatch(getCompanyList());
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  addNewVendorAssignee() {
    const selectedVendor = this.vendorList.find(v => v.value === this.form.controls.vendor.value);
    if (selectedVendor) {
      this.modalsService
        .openVendorUserModal(selectedVendor.value, `Add employee and assign to ${this.data.column.name}`)
        .afterClosed()
        .pipe(
          untilDestroyed(this),
          tap(data => {
            if (data?.invitedUser) {
              this.store.dispatch(getCompanyList());
              this.companiesService.getCompany(selectedVendor.value).subscribe(company => {
                this.vendorAssigneeList = getEmployeeRadioList(company.employees);
                this.form.controls.assignee.setValue(data.invitedUser.id);
                this.cdr.detectChanges();
              });
            }
          })
        )
        .subscribe();
    }
  }
}
